<template>
  <b-modal
    v-model="showModal"
    centered
    no-close-on-backdrop
    no-close-on-esc
    hide-header-close
    hide-footer
    size="md"
    body-class="p-0"
  >
    <div class="suspended-overlay">
      <!-- Icon -->
      <div class="icon-container mb-1">
        <feather-icon icon="AlertOctagonIcon" size="48" class="text-danger" />
      </div>

      <!-- Title -->
      <h3 class="text-danger mb-50">Layanan Ditangguhkan</h3>

      <!-- Message for Owner -->
      <template v-if="isOwner">
        <p class="mb-1">
          Langganan fasilitas <span class="entity-name">{{ entityName }}</span> telah ditangguhkan.
          Silakan perpanjang langganan untuk mengaktifkan kembali layanan.
        </p>

        <b-button
          variant="primary"
          block
          class="mb-50"
          @click="goToSubscription"
        >
          <feather-icon icon="CreditCardIcon" size="16" class="mr-50" />
          Perpanjang Langganan
        </b-button>

        <b-button
          variant="outline-success"
          block
          :href="whatsappUrl"
          target="_blank"
        >
          <feather-icon icon="MessageCircleIcon" size="16" class="mr-50" />
          Hubungi Customer Service
        </b-button>
      </template>

      <!-- Message for Regular User -->
      <template v-else>
        <p class="mb-1">
          Layanan untuk fasilitas <span class="entity-name">{{ entityName }}</span> sedang ditangguhkan.
        </p>

        <b-alert variant="warning" show class="mb-1 text-left">
          <div class="alert-body">
            <feather-icon icon="InfoIcon" class="mr-50" />
            Silakan hubungi pemilik fasilitas atau administrator Anda untuk informasi lebih lanjut.
          </div>
        </b-alert>

        <div class="contact-section">
          <p class="mb-50 text-secondary" style="font-size: 0.85rem">
            Atau hubungi Customer Service kami:
          </p>

          <b-button
            variant="success"
            block
            :href="whatsappUrl"
            target="_blank"
          >
            <feather-icon icon="MessageCircleIcon" size="16" class="mr-50" />
            Chat via WhatsApp
          </b-button>

          <p class="mt-50 mb-0 text-secondary" style="font-size: 0.8rem">
            <feather-icon icon="PhoneIcon" size="12" class="mr-25" />
            {{ customerServicePhone }}
          </p>
        </div>
      </template>

      <!-- Logout Option -->
      <div class="mt-1 pt-1 border-top">
        <b-button
          variant="outline-warning"
          size="sm"
          @click="logout"
        >
          <feather-icon icon="LogOutIcon" size="14" class="mr-25" />
          Keluar dari akun
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted } from '@vue/composition-api'
import { BModal, BButton, BAlert } from 'bootstrap-vue'
import { useAuthStore } from '@/pinia/auth'
import { useWorkspaceStore } from '@/pinia/workspace'

export default defineComponent({
  name: 'SuspendedTenantOverlay',
  components: {
    BModal,
    BButton,
    BAlert,
  },
  setup(props, { root }) {
    const authStore = useAuthStore()
    const workspaceStore = useWorkspaceStore()

    const showModal = ref(false)
    const customerServicePhone = '+62 819-0888-8894'

    const isOwner = computed(() => workspaceStore.isOwner)
    const entityName = computed(() => authStore.entity?.name || 'Anda')
    const subscriptionStatus = computed(() => authStore.subscriptionStatus)

    const whatsappMessage = computed(() => {
      const name = entityName.value
      if (isOwner.value) {
        return `Halo, saya pemilik ${name}. Saya ingin bertanya mengenai perpanjangan langganan Dekacare.`
      }
      return `Halo, saya pengguna dari ${name}. Saya ingin bertanya mengenai status layanan yang sedang ditangguhkan.`
    })

    const whatsappUrl = computed(() => {
      const phone = customerServicePhone.replace(/[^0-9]/g, '')
      const message = encodeURIComponent(whatsappMessage.value)
      return `https://wa.me/${phone}?text=${message}`
    })

    const checkAndShowOverlay = async () => {
      // Only show for suspended status
      if (subscriptionStatus.value !== 'suspended') {
        showModal.value = false
        return
      }

      // Check owner status if not already checked
      if (!workspaceStore.ownerStatusChecked) {
        try {
          await workspaceStore.checkOwnerStatus()
        } catch (e) {
          // If check fails, assume not owner
        }
      }

      showModal.value = true
    }

    const goToSubscription = () => {
      showModal.value = false
      root.$router.push({ name: 'workspace', query: { tab: 'berlangganan' } })
    }

    const logout = () => {
      showModal.value = false
      authStore.signOut()
      // Force full page redirect to ensure clean state
      window.location.href = '/login'
    }

    // Watch for subscription status changes
    watch(subscriptionStatus, (newStatus) => {
      if (newStatus === 'suspended') {
        checkAndShowOverlay()
      } else {
        showModal.value = false
      }
    }, { immediate: true })

    // Also check on mount
    onMounted(() => {
      checkAndShowOverlay()
    })

    return {
      showModal,
      isOwner,
      entityName,
      customerServicePhone,
      whatsappUrl,
      goToSubscription,
      logout,
    }
  },
})
</script>

<style lang="scss" scoped>
.suspended-overlay {
  padding: 1.5rem;
  text-align: center;

  p {
    color: #333;
  }

  .entity-name {
    font-weight: 700;
    color: #5e5873;
    font-size: 1.1em;
  }

  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    margin: 0 auto;
    border-radius: 50%;
    background-color: rgba(234, 84, 85, 0.12);
  }

  .contact-section {
    background-color: #f8f8f8;
    border-radius: 0.428rem;
    padding: 1rem;
    margin-top: 1rem;
  }
}
</style>
